<template>
  <v-container fluid>
    <v-card>
      <v-toolbar
        color="indigo"
        dark>

        <v-toolbar-title>
          <v-icon left>{{ toolbarIcon }}</v-icon>
          {{ $vuetify.lang.t('$vuetify.reports.title') }}
        </v-toolbar-title>

        <v-progress-linear :active="isLoading" indeterminate absolute bottom>
        </v-progress-linear>
      </v-toolbar>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <datetime-chart-filter :start-date="`${localFilters.date.start.date}`"
                                   :finish-date="`${localFilters.date.finish.date}`"
                                   :trim-date="false"
                                   :header="$vuetify.lang.t('$vuetify.charts.filters.date.title')"
                                   @datetime-changed="setDatetime"></datetime-chart-filter>
          </v-col>
          <v-col cols="12" md="6">
            <drivers-search-autocomplete @driver-choice="setDriver" :nano-users="true"
                                         :multiply-choice="true"></drivers-search-autocomplete>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="px-1 py-1">
        <v-spacer></v-spacer>
        <v-btn text color="light-green" class="mx-2" @click="downloadReport">{{
            $vuetify.lang.t('$vuetify.reports.generateButton')
          }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-row>
      <v-col cols="12" md="6">
        <v-card>
          <v-toolbar
            color="indigo"
            dark>

            <v-toolbar-title>
              <v-icon left>{{ toolbarIcon }}</v-icon>
              {{ $vuetify.lang.t('$vuetify.reports.criticalArchive.title') }}
            </v-toolbar-title>

            <v-progress-linear :active="isSlidesLoading" indeterminate absolute bottom>
            </v-progress-linear>
          </v-toolbar>
          <v-card-text>
            <datetime-chart-filter :start-date="`${localSlidesFilters.date.start.date}`"
                                   :finish-date="`${localSlidesFilters.date.finish.date}`"
                                   :trim-date="false"
                                   :header="$vuetify.lang.t('$vuetify.charts.filters.date.title')"
                                   @datetime-changed="setSlidesDatetime"></datetime-chart-filter>
          </v-card-text>
          <v-card-actions class="px-1 py-1">
            <v-spacer></v-spacer>
            <v-btn text color="light-green" class="mx-2" @click="downloadSlidesReport">{{
                $vuetify.lang.t('$vuetify.reports.criticalArchive.download')
              }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card>
          <v-toolbar
            color="indigo"
            dark>

            <v-toolbar-title>
              <v-icon left>{{ toolbarIcon }}</v-icon>
              {{ $vuetify.lang.t('$vuetify.reports.datasetTitle') }}
            </v-toolbar-title>

            <v-progress-linear :active="isDatasetLoading" indeterminate absolute bottom>
            </v-progress-linear>

            <v-spacer></v-spacer>
            <v-btn text color="orange" class="mx-2" @click="downloadDatasetReport">{{
                $vuetify.lang.t('$vuetify.reports.generateButton')
              }}
            </v-btn>
          </v-toolbar>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mdiFileChart } from '@mdi/js'
import { getCurrentTime } from '@/utils/time'
import { defaultChartDatetimeState } from '@/utils/filters'
import DatetimeChartFilter from '../../components/charts/utils/datetimeChartFilter'
import DriversSearchAutocomplete from '../../components/charts/DriversSearchAutocomplete'
import snackbarMixin from '../../mixins/snackbarMixin'
import backend from '../../backend'
import { getCurrentTimezoneForBackend } from '@/utils/timezone'

const { _currentYear, _currentMonth, _lastDay } = getCurrentTime()

const getDefaultFilters = () => ({
  date: {
    start: defaultChartDatetimeState(`${_currentYear}-${_currentMonth}-01`),
    finish: defaultChartDatetimeState(`${_currentYear}-${_currentMonth}-${_lastDay}`)
  },
  drivers: []
})

export default {
  name: 'Reports',
  components: { DriversSearchAutocomplete, DatetimeChartFilter },
  mixins: [snackbarMixin],
  data: () => ({
    localFilters: getDefaultFilters(), // local copy for filter card only
    localSlidesFilters: getDefaultFilters(), // local copy for filter card only

    isLoading: false,
    isSlidesLoading: false,
    isDatasetLoading: false,

    toolbarIcon: mdiFileChart
  }),
  methods: {
    setDatetime (datetime) {
      this.localFilters.date[datetime.type][datetime.mode] = datetime.value
    },
    setSlidesDatetime (datetime) {
      this.localSlidesFilters.date[datetime.type][datetime.mode] = datetime.value
    },
    setDriver (driver) {
      this.localFilters.drivers = driver
    },
    async downloadReport () {
      this.isLoading = true

      try {
        const result = await backend.downloadSummaryReport({
          ...this.localFilters,
          timezone: getCurrentTimezoneForBackend()
        })

        const url = window.URL.createObjectURL(new Blob([result.data], { type: result.headers['content-type'] }))
        const link = document.createElement('a')
        link.href = url

        link.setAttribute('download', result.headers.filename)
        link.click()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        this.processFailedRequest(e)
      } finally {
        this.isLoading = false
      }
    },
    async downloadSlidesReport() {
      this.isSlidesLoading = true

      try {
        const result = await backend.downloadSlidesArchive({
          ...this.localSlidesFilters,
          timezone: getCurrentTimezoneForBackend()
        })

        // console.log(result.data)

        const url = window.URL.createObjectURL(new Blob([result.data], { type: result.headers['content-type'] }))
        const link = document.createElement('a')
        link.href = url

        link.setAttribute('download', result.headers.filename)
        link.click()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        this.processFailedRequest(e)
      } finally {
        this.isSlidesLoading = false
      }
    },
    async downloadDatasetReport () {
      this.isDatasetLoading = true

      try {
        const result = await backend.downloadCriticalDatasetReport({})

        const url = window.URL.createObjectURL(new Blob([result.data], { type: result.headers['content-type'] }))
        const link = document.createElement('a')
        link.href = url

        link.setAttribute('download', result.headers.filename)
        link.click()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        this.processFailedRequest(e)
      } finally {
        this.isDatasetLoading = false
      }
    }
  }
}
</script>

<style scoped>

</style>
